import { useEffect, useState } from "react";

import { LoadingSpinner } from "@newsela/angelou";
import { useRouter } from "next/router";

import ChallengeLandingHeader from "@/challenges/ChallengeLandingHeader";
import ChallengesFolder from "@/challenges/ChallengesFolder";
import ChallengesTeacher from "@/challenges/ChallengesTeacher/ChallengesTeacher";
import ChallengeTeacherHomePage from "@/challenges/ChallengeTeacherHomePage/ChallengeTeacherHomePage";
import { Challenge } from "@/challenges/utils/interfaces";
import useChallengesGet from "@/challenges/utils/useChallengesGet";
import { challengeWrapper, loadingWrapper } from "./ChallengeWrapperView.style";

interface ChallengeWrapperViewProps {
  isHomeVersion: boolean;
  isTeacher: boolean;
  setNoData: (isNoData: boolean) => void;
}

const ChallengeWrapperView = ({
  isHomeVersion,
  isTeacher = false,
  setNoData,
}: ChallengeWrapperViewProps) => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [activeData, setActiveData] = useState<Challenge | null>(null);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<Challenge[] | null>(null);
  const [error, setError] = useState<string | Error | null>(null);

  const router = useRouter();
  const [getChallenges] = useChallengesGet();

  const updateChallengesData = async () => {
    const now = new Date();
    const { data, error } = await getChallenges({
      month: now.getMonth() + 1,
      year: now.getFullYear(),
    });
    setData(data?.data);
    setError(data?.error_message || error);
  };

  useEffect(() => {
    updateChallengesData();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (!error && data?.[0]) {
      setActiveTab(data[0].subject);
      setIsLoading(false);
      setNoData(false);
    } else if (error) {
      setNoData(true);
      if (!isHomeVersion) {
        router.push("/home");
      }
      setIsLoading(false);
    }
  }, [data, error]);

  useEffect(() => {
    if (activeTab && data) {
      setActiveData(
        data?.find((challenge) => challenge.subject === activeTab) || null,
      );
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeData) {
      setExpirationDate(new Date(activeData?.expiration) || new Date());
    }
  }, [activeData]);
  return isLoading && !error ? (
    <div css={loadingWrapper}>
      <LoadingSpinner />
    </div>
  ) : error || data?.length === 0 ? (
    <> </>
  ) : isTeacher ? (
    isHomeVersion ? (
      <ChallengeTeacherHomePage expirationDate={expirationDate} />
    ) : (
      <ChallengesTeacher
        expirationDate={expirationDate}
        subjects={data?.map(({ subject }) => subject)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        activeData={activeData}
      />
    )
  ) : (
    <div css={challengeWrapper(isHomeVersion)}>
      <ChallengeLandingHeader expirationDate={expirationDate} />
      <ChallengesFolder isHomeVersion={isHomeVersion} challengesData={data} />
    </div>
  );
};

export default ChallengeWrapperView;
